<template>
    <div class="flex flex-col gap-y-2">
        <div class="flex items-center justify-between">
            <div class="text-2xl font-medium text-gray-900">
                {{ title }}
            </div>

            <prime-button
                text
                icon="pi pi-pencil"
                label="Edit"
                :class="editing ? 'invisible' : ''"
                :disabled="isProcessing"
                @click="$emit('update:editing', true)"
            />
        </div>

        <div
            v-if="!editing"
            class="grid grid-cols-2 gap-y-2"
        >
            <div
                v-if="isFullAddress"
                class="col-span-2"
            >
                <div class="text-sm font-medium text-gray-500">Address</div>
                <div class="text-gray-900">
                    {{ address1 }}
                </div>
                <div v-if="address2 !== ''">
                    {{ address2 }}
                </div>
            </div>

            <div v-if="isFullAddress">
                <div class="text-sm font-medium text-gray-500">City</div>
                <div class="text-gray-900">
                    {{ city }}
                </div>
            </div>

            <div v-if="isFullAddress">
                <div class="text-sm font-medium text-gray-500">State</div>
                <div class="text-gray-900">
                    {{ state }}
                </div>
            </div>

            <div>
                <div class="text-sm font-medium text-gray-500">Postal Code</div>
                <div class="text-gray-900">
                    {{ postal }}
                </div>
            </div>

            <div>
                <div class="text-sm font-medium text-gray-500">Country</div>
                <div class="text-gray-900">
                    {{ country }}
                </div>
            </div>
        </div>

        <div
            v-else
            class="grid grid-cols-12 gap-x-4 gap-y-1"
        >
            <div class="col-span-12">
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span>Country
                </div>
                <prime-auto-complete
                    id="country"
                    v-model="selectedCountry"
                    autocomplete="country"
                    placeholder="Country"
                    option-label="name"
                    data-key="code"
                    :suggestions="filteredCountries"
                    dropdown
                    type="text"
                    class="w-full"
                    :disabled="isProcessing"
                    :pt="{ input: { class: 'w-full' } }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                    @complete="searchCountry"
                />
                <div class="text-red-600 text-sm">
                    {{ errorCountry || '&nbsp;' }}
                </div>
            </div>
            <div
                v-if="isFullAddress"
                class="col-span-12"
            >
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span>Address Line One
                </div>
                <input-text
                    id="billing_address_line1"
                    v-model="address1"
                    placeholder="Address Line One"
                    autocomplete="billing address-line1"
                    type="text"
                    class="w-full"
                    :class="errorAddress1 ? 'border-red-600' : ''"
                    :disabled="isProcessing"
                />
                <div class="text-red-600 text-sm">
                    {{ errorAddress1 || '&nbsp;' }}
                </div>
            </div>
            <div
                v-if="isFullAddress"
                class="col-span-12"
            >
                <div class="text-sm text-gray-500">
                    Address Line Two (Optional)
                </div>
                <input-text
                    id="billing_address_line2"
                    v-model="address2"
                    placeholder="Address Line Two"
                    autocomplete="billing address-line2"
                    type="text"
                    class="w-full"
                    :class="errorAddress2 ? 'border-red-600' : ''"
                    :disabled="isProcessing"
                />
                <div class="text-red-600 text-sm">
                    {{ errorAddress2 || '&nbsp;' }}
                </div>
            </div>
            <div
                v-if="isFullAddress"
                class="col-span-12 lg:col-span-6"
            >
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span>City
                </div>
                <input-text
                    id="city"
                    v-model="city"
                    autocomplete="billing address-level2"
                    placeholder="City"
                    type="text"
                    class="w-full"
                    :class="errorCity ? 'border-red-600' : ''"
                    :disabled="isProcessing"
                />
                <div class="text-red-600 text-sm">
                    {{ errorCity || '&nbsp;' }}
                </div>
            </div>
            <div
                v-if="isFullAddress"
                class="col-span-12 lg:col-span-6"
            >
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span
                    >{{
                        isUnitedStates
                            ? $t('address.state')
                            : $t('address.stateOrProvince')
                    }}
                </div>
                <prime-dropdown
                    v-if="isUnitedStates"
                    v-model="state"
                    option-label="name"
                    option-value="code"
                    :options="unitedStatesCodes"
                    class="!w-full"
                    :class="errorState ? 'border-red-600' : ''"
                    :disabled="isProcessing"
                />
                <input-text
                    v-else
                    id="state"
                    v-model="state"
                    autocomplete="billing address-level1"
                    placeholder="State/Province"
                    type="text"
                    class="w-full"
                    :class="errorState ? 'border-red-600' : ''"
                    :disabled="isProcessing"
                />
                <div class="text-red-600 text-sm">
                    {{ errorState || '&nbsp;' }}
                </div>
            </div>
            <div class="col-span-12">
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span>Postal Code
                </div>
                <input-text
                    id="postal_code"
                    v-model="postal"
                    autocomplete="postal-code"
                    placeholder="Postal Code"
                    type="text"
                    class="w-full"
                    :class="errorPostal ? 'border-red-600' : ''"
                    :disabled="isProcessing"
                />
                <div class="text-red-600 text-sm">
                    {{ errorPostal || '&nbsp;' }}
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { boolean, object, string } from 'yup'
import {
    type FragmentType,
    getFragmentData,
    graphql
} from '~/resources/graphql'
import { toTypedSchema } from '@vee-validate/yup'
import type { TypedSchema } from 'vee-validate'

export const BillingAddressCustomerFragment = graphql(/* GraphQL */ `
    fragment BillingAddressCustomerFragment on Customer {
        address1
        address2
        city
        state
        postal
        country
    }
`)

export const BillingAddressSchema = object({
    fullAddress: boolean().required(),
    address1: string()
        .label('Address Line One')
        .when('fullAddress', {
            is: true,
            then: () => string().required('Please enter an address')
        }),
    address2: string()
        .label('Address Line Two')
        .when('fullAddress', {
            is: true,
            then: () => string()
        }),
    city: string()
        .label('City')
        .when('fullAddress', {
            is: true,
            then: () => string().required('Please enter a city')
        }),
    state: string()
        .label('State')
        .when('fullAddress', {
            is: true,
            then: () => string().required('Please enter a state')
        }),
    postal: string().required().label('Postal code'),
    country: string().required().label('Country')
}).required()

export function BillingAddressInitialValues(
    customer: FragmentType<typeof BillingAddressCustomerFragment> | null,
    isFullAddress: boolean
) {
    const customerData =
        customer === null
            ? null
            : getFragmentData(BillingAddressCustomerFragment, customer)

    return {
        fullAddress: isFullAddress,
        address1: customerData?.address1 ?? '',
        address2: customerData?.address2 ?? '',
        city: customerData?.city ?? '',
        state: customerData?.state ?? '',
        postal: customerData?.postal ?? '',
        country: customerData?.country ?? ''
    }
}

export const billingAddressSchemaTyped = toTypedSchema(BillingAddressSchema)
export type BillingAddressSchemaTyped =
    typeof billingAddressSchemaTyped extends TypedSchema<unknown, infer TOutput>
        ? TOutput
        : never
</script>

<script setup lang="ts">
import unitedStatesCodes from 'assets/us-code.json'
import InputText from 'primevue/inputtext'
import PrimeDropdown from 'primevue/dropdown'
import PrimeButton from 'primevue/button'
import PrimeAutoComplete from 'primevue/autocomplete'
import { computed, onMounted, ref, watch } from 'vue'
import { useField } from 'vee-validate'
import countryCodes from 'assets/country-code.json'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useShowAlert } from '#imports'
import { useCheckoutFormProcessing } from '~/components/Checkout/Forms/use-checkout-form'

const props = defineProps<{
    customer: FragmentType<typeof BillingAddressCustomerFragment> | null
    isFullAddress: boolean
    title: string
    editing: boolean
}>()

const emit = defineEmits<{
    (e: 'update:editing', payload: boolean): void
}>()

const customerData = computed(() =>
    props.customer === null
        ? null
        : getFragmentData(BillingAddressCustomerFragment, props.customer)
)

const hasCompleteAddress = computed(() => {
    if (customerData.value === null) {
        return false
    }

    if (!props.isFullAddress) {
        return (
            customerData.value.postal !== '' &&
            customerData.value.country !== ''
        )
    }

    return (
        customerData.value.address1 !== '' &&
        customerData.value.city !== '' &&
        customerData.value.state !== '' &&
        customerData.value.postal !== '' &&
        customerData.value.country !== ''
    )
})

emit('update:editing', !hasCompleteAddress.value)
const { value: address1, errorMessage: errorAddress1 } =
    useField('address.address1')
const { value: address2, errorMessage: errorAddress2 } =
    useField('address.address2')
const { value: city, errorMessage: errorCity } = useField('address.city')
const { value: state, errorMessage: errorState } = useField('address.state')
const { value: postal, errorMessage: errorPostal } = useField('address.postal')
const { value: country, errorMessage: errorCountry } =
    useField('address.country')

watch(
    [
        errorAddress1,
        errorAddress2,
        errorCity,
        errorState,
        errorPostal,
        errorCountry
    ],
    ([
        newValueAddress1,
        newValueAddress2,
        newValueCity,
        newValueState,
        newValuePostal,
        newValueCountry
    ]) => {
        if (
            newValueAddress1 ||
            newValueAddress2 ||
            newValueCity ||
            newValueState ||
            newValuePostal ||
            newValueCountry
        ) {
            emit('update:editing', true)
        }
    }
)

const unitedStatesOption = { code: 'US', name: 'United States' }
const countryOptions = [
    unitedStatesOption,
    ...(countryCodes as unknown as { code: string; name: string }[]).filter(
        option => option.code !== 'US'
    )
]
const filteredCountries = ref<{ code: string; name: string }[]>([])
const selectedCountry = ref<{ code: string; name: string } | string>(
    countryOptions.find(option => option.code === country.value) ?? ''
)
const isUnitedStates = computed(() => country.value === 'US')

const showAlert = useShowAlert()
watch(selectedCountry, (newValue: { code: string; name: string } | string) => {
    // If newValue is an object (they clicked one of the options in the list)
    if (typeof newValue === 'object') {
        if (newValue.code.length > 64) {
            showAlert('Error', 'Country code must be less than 64 characters')
            country.value = undefined
        } else {
            country.value = newValue.code
        }
    }
    // Else newValue is a string (it came from typing)
    else {
        // If they typed one of the country names (case-insensitive)
        // Then we select that country for them
        const matchingCountry = countryOptions.find(
            option => option.name.toLowerCase() === newValue.toLowerCase()
        )
        if (matchingCountry === undefined) {
            country.value = undefined
        } else {
            country.value = matchingCountry.code
            selectedCountry.value = matchingCountry
        }
    }
})

function searchCountry(event: { query: string }): void {
    if (!event.query.trim().length) {
        filteredCountries.value = [...countryOptions]
    } else {
        filteredCountries.value = countryOptions.filter(option => {
            return (
                option.name
                    .toLowerCase()
                    .startsWith(event.query.toLowerCase()) ||
                option.code.toLowerCase().startsWith(event.query.toLowerCase())
            )
        })
    }
}

const activeFacility = useActiveFacility()
onMounted(() => {
    if (!hasCompleteAddress.value) {
        const facilityCountry = countryOptions.find(
            country => country.code === activeFacility.value.country
        )
        if (facilityCountry === undefined) {
            throw new Error('Cannot find facility setting country')
        }

        selectedCountry.value = facilityCountry
    }
})

const { isProcessing } = useCheckoutFormProcessing()
</script>
